import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';
import { MediaUpload, UploadedMedia } from '@/components/Media/MediaUpload';
import { MediaLibrary } from '@/components/Media/MediaLibrary';
import { ImageSizeSelector, ImageSize } from '@/components/Media/ImageSizeSelector';

/**
 * Pending image drop state.
 * Stores the media item and drop position while the size selector is shown.
 */
interface PendingImageDrop {
    media: UploadedMedia;
    position: { x: number; y: number };
}

/**
 * Tome page data from the backend.
 */
interface TomePage {
    id: number;
    name: string;
    slug: string;
    content: string | null;
    chapter_id: number | null;
    book_id: number | null;
    order: number;
    is_published: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Book with chapters for the dropdown.
 */
interface TomeBook {
    id: number;
    title: string;
    chapters: { id: number; title: string }[];
}

/**
 * Props for the Edit page.
 */
interface Props {
    page: TomePage;
    media: UploadedMedia[];
    books: TomeBook[];
}

/**
 * GM Tome Edit page - form to edit an existing tome page.
 */
export default function Edit({ page, media: initialMedia, books }: Props) {
    // Form fields - pre-populated from page
    const [name, setName] = useState(page.name);
    const [content, setContent] = useState(page.content || '');
    const [chapterId, setChapterId] = useState(page.chapter_id?.toString() || '');
    const [isPublished, setIsPublished] = useState(page.is_published);

    // Media state for image uploads
    const [mediaList, setMediaList] = useState<UploadedMedia[]>(initialMedia);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Image drop state - tracks pending image insertion with size selection
    const [pendingImageDrop, setPendingImageDrop] = useState<PendingImageDrop | null>(null);

    /**
     * Handle successful media upload - add to media list.
     */
    const handleMediaUpload = (media: UploadedMedia) => {
        setMediaList(prev => [media, ...prev]);
    };

    /**
     * Handle media deletion - remove from media list.
     */
    const handleMediaDelete = (mediaId: number) => {
        setMediaList(prev => prev.filter(m => m.id !== mediaId));
    };

    /**
     * Handle image drop from MediaLibrary onto the Editor.
     * Shows the size selector at the drop position.
     */
    const handleImageDrop = useCallback((media: UploadedMedia, position: { x: number; y: number }) => {
        setPendingImageDrop({ media, position });
    }, []);

    /**
     * Handle size selection from ImageSizeSelector.
     * Inserts the markdown image into the content field.
     */
    const handleImageSizeSelect = useCallback((size: ImageSize, media: UploadedMedia) => {
        if (!pendingImageDrop) return;

        // Get the appropriate URL based on selected size
        const url = media.urls[size];
        const altText = media.alt_text || media.original_filename;
        const markdown = `![${altText}](${url})`;

        // Insert into content
        setContent(prev => prev ? `${prev}\n\n${markdown}` : markdown);

        // Clear the pending drop
        setPendingImageDrop(null);
    }, [pendingImageDrop]);

    /**
     * Cancel the image size selection.
     */
    const handleImageDropCancel = useCallback(() => {
        setPendingImageDrop(null);
    }, []);

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.patch(`/gm/tome/${page.id}`, {
            name,
            content: content || null,
            chapter_id: chapterId,
            is_published: isPublished,
        }, {
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title={`Edit: ${page.name}`} activePath="/gm/tome">
            {/* Header - full width above the two-column layout */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-white">📖 Edit Tome Page</h1>
                    {/* Navigate back to the book's show page if book_id is available, otherwise to books list */}
                    <Link
                        href={page.book_id ? `/gm/tome/books/${page.book_id}` : '/gm/tome/books'}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ← Back to Book
                    </Link>
                </div>
            </div>

            {/* Two-column layout: form on left, sticky media sidebar on right */}
            <div className="flex flex-col lg:flex-row lg:gap-6">
                {/* Main form content - left column */}
                <div className="flex-1 min-w-0 lg:max-w-3xl">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Info Section */}
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                            <h2 className="text-lg font-semibold text-white mb-4">Page Information</h2>

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Page Title <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    required
                                />
                                {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
                            </div>

                            {/* Slug (read-only) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">URL Slug</label>
                                <div className="px-3 py-2 bg-slate-900 border border-slate-700 rounded text-gray-400 text-sm">
                                    /tome/{page.slug}
                                </div>
                            </div>

                            {/* Chapter Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Chapter <span className="text-red-400">*</span>
                                </label>
                                <select
                                    value={chapterId}
                                    onChange={(e) => setChapterId(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    required
                                >
                                    <option value="">Select a chapter...</option>
                                    {books.map((book) => (
                                        <optgroup key={book.id} label={book.title}>
                                            {book.chapters.map((chapter) => (
                                                <option key={chapter.id} value={chapter.id}>
                                                    {chapter.title}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                {errors.chapter_id && <p className="mt-1 text-sm text-red-400">{errors.chapter_id}</p>}
                            </div>

                            {/* Content - Markdown Editor */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
                                <p className="text-xs text-gray-500 mb-2">
                                    Drag images from the sidebar to insert them into your content.
                                </p>
                                <div data-color-mode="dark">
                                    <Editor
                                        value={content}
                                        onChange={(value) => setContent(value)}
                                        placeholder="Write your lore content here..."
                                        height={400}
                                        onImageDrop={handleImageDrop}
                                    />
                                </div>
                                {errors.content && <p className="mt-1 text-sm text-red-400">{errors.content}</p>}
                            </div>

                            {/* Published Status */}
                            <div className="flex items-center">
                                <label className="flex items-center gap-2 text-sm text-gray-300">
                                    <input
                                        type="checkbox"
                                        checked={isPublished}
                                        onChange={(e) => setIsPublished(e.target.checked)}
                                        className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                    />
                                    Published (visible to all users)
                                </label>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex gap-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                            <Link
                                href="/gm/tome"
                                className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Close
                            </Link>
                            {page.is_published && (
                                <a
                                    href={`/tome/page/${page.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                >
                                    View Page
                                </a>
                            )}
                        </div>
                    </form>
                </div>

                {/* Media Sidebar - right column, sticky on large screens */}
                <div className="mt-6 lg:mt-0 lg:w-80 xl:w-96 flex-shrink-0">
                    <div className="lg:sticky lg:top-4">
                        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-4">
                            <h2 className="text-lg font-semibold text-white">Images</h2>
                            <p className="text-sm text-gray-400">
                                Upload images to use in your tome page.
                                Drag images to the editor or click "Copy Link" for markdown syntax.
                            </p>

                            {/* Upload area */}
                            <MediaUpload
                                uploadUrl={`/gm/tome/${page.id}/media`}
                                onUploadComplete={handleMediaUpload}
                                onUploadError={(error) => console.error('Upload error:', error)}
                            />

                            {/* Media library grid - scrollable if many images */}
                            {mediaList.length > 0 && (
                                <div className="mt-4">
                                    <h3 className="text-sm font-medium text-gray-300 mb-2">
                                        Uploaded Images ({mediaList.length})
                                    </h3>
                                    <div className="max-h-96 overflow-y-auto">
                                        <MediaLibrary
                                            media={mediaList}
                                            deleteUrlBase={`/gm/tome/${page.id}/media`}
                                            onDelete={handleMediaDelete}
                                            canDelete={true}
                                            draggable={true}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Image size selector modal - shown when dropping an image onto the editor */}
            {pendingImageDrop && (
                <ImageSizeSelector
                    media={pendingImageDrop.media}
                    position={pendingImageDrop.position}
                    onSelect={handleImageSizeSelect}
                    onCancel={handleImageDropCancel}
                />
            )}
        </GmLayout>
    );
}

